(() => {
  const STRICT_SEARCH_FIELDS = 'title,product_type,tag,vendor,variants.title,variants.sku,body';
  const REQUIRED_SEARCH_PARAMS = {
    type: 'product',
    'options[unavailable_products]': 'last',
    'options[prefix]': 'none',
    'options[fields]': STRICT_SEARCH_FIELDS
  };

  const STOP_WORDS = new Set([
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'in', 'into', 'is', 'it', 'of', 'on', 'or', 'the', 'to', 'with'
  ]);

  const SYNONYM_GROUPS = [
    ['acu', 'ucp', 'digital'],
    ['ocp', 'multicam', 'multi cam', 'multicam ocp'],
    ['m81', 'woodland', 'woodland m81'],
    ['dcu', 'desert', 'three color desert', '3 color desert'],
    ['aor1', 'aor 1', 'nwU type ii', 'type ii'],
    ['aor2', 'aor 2', 'nwU type iii', 'type iii'],
    ['mtp', 'british mtp'],
    ['flecktarn', 'german flecktarn'],
    ['coyote', 'coyote tan', 'tan 499', 'tan499', '499', 'tan'],
    ['od', 'olive', 'olive drab', 'green', 'od green'],
    ['foliage', 'foliage green'],
    ['grey', 'gray'],
    ['ruck', 'rucksack', 'rucksacks', 'field pack', 'main pack'],
    ['pack', 'packs', 'backpack', 'backpacks'],
    ['pouch', 'pouches'],
    ['bag', 'bags'],
    ['case', 'cases'],
    ['jacket', 'jackets', 'parka', 'parkas', 'coat', 'coats'],
    ['shirt', 'shirts', 'top', 'tops', 'blouse', 'blouses'],
    ['pant', 'pants', 'trouser', 'trousers', 'bottom', 'bottoms'],
    ['short', 'shorts'],
    ['boot', 'boots', 'footwear'],
    ['hat', 'hats', 'cap', 'caps', 'cover', 'covers', 'headgear'],
    ['helmet', 'helmets'],
    ['kneepad', 'kneepads', 'knee pad', 'knee pads'],
    ['pad', 'pads'],
    ['glove', 'gloves'],
    ['belt', 'belts'],
    ['suspender', 'suspenders'],
    ['liner', 'liners'],
    ['british', 'uk', 'u k', 'united kingdom', 'england'],
    ['german', 'germany', 'deutschland'],
    ['austrian', 'austria'],
    ['italian', 'italy'],
    ['dutch', 'netherlands', 'holland'],
    ['french', 'france'],
    ['canadian', 'canada'],
    ['spanish', 'spain'],
    ['swedish', 'sweden'],
    ['norwegian', 'norway'],
    ['danish', 'denmark'],
    ['us', 'usa', 'u s', 'american', 'united states'],
    ['usmc', 'marine', 'marines', 'marine corps'],
    ['army', 'usa army', 'u s army'],
    ['navy', 'naval'],
    ['air force', 'usaf']
  ];

  const CATEGORY_TERMS = new Set([
    'ruck', 'rucksack', 'pack', 'backpack', 'field pack', 'main pack',
    'pouch', 'bag', 'case',
    'jacket', 'parka', 'coat', 'shirt', 'top', 'blouse', 'pant', 'pants', 'trouser', 'trousers', 'bottom', 'bottoms', 'short', 'shorts',
    'boot', 'boots', 'footwear', 'hat', 'cap', 'cover', 'headgear', 'helmet',
    'kneepad', 'kneepads', 'knee', 'pad', 'pads', 'glove', 'belt', 'suspender', 'liner', 'vest', 'carrier', 'strap', 'mat', 'sleeping', 'bivy', 'poncho'
  ]);

  const COUNTRY_TERMS = new Set([
    'british', 'uk', 'u k', 'united kingdom', 'england', 'german', 'germany', 'deutschland', 'austrian', 'austria', 'italian', 'italy',
    'dutch', 'netherlands', 'holland', 'french', 'france', 'canadian', 'canada', 'spanish', 'spain', 'swedish', 'sweden',
    'norwegian', 'norway', 'danish', 'denmark', 'us', 'usa', 'u s', 'american', 'united states'
  ]);

  const COLOR_TERMS = new Set([
    'acu', 'ucp', 'digital', 'ocp', 'multicam', 'multi cam', 'm81', 'woodland', 'dcu', 'desert', 'aor1', 'aor 1', 'aor2', 'aor 2',
    'mtp', 'flecktarn', 'coyote', 'coyote tan', 'tan', 'tan 499', 'tan499', '499', 'od', 'olive', 'olive drab', 'green', 'foliage',
    'foliage green', 'black', 'white', 'brown', 'khaki', 'sage', 'grey', 'gray', 'navy'
  ]);

  let latestRun = 0;
  let isRendering = false;
  let observer = null;

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[’']/g, '')
      .replace(/[\/_\-]+/g, ' ')
      .replace(/[^a-z0-9+.#\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function singularize(term) {
    const normalized = normalize(term);
    if (normalized.length > 4 && normalized.endsWith('ies')) return normalized.slice(0, -3) + 'y';
    if (normalized.length > 4 && normalized.endsWith('sses')) return normalized.slice(0, -2);
    if (normalized.length > 3 && normalized.endsWith('ches')) return normalized.slice(0, -2);
    if (normalized.length > 3 && normalized.endsWith('shes')) return normalized.slice(0, -2);
    if (normalized.length > 3 && normalized.endsWith('xes')) return normalized.slice(0, -2);
    if (normalized.length > 3 && normalized.endsWith('s') && !normalized.endsWith('ss')) return normalized.slice(0, -1);
    return normalized;
  }

  function normalizedSetFrom(values) {
    const set = new Set();
    values.forEach((value) => {
      const normalized = normalize(value);
      if (!normalized) return;
      set.add(normalized);
      set.add(singularize(normalized));
    });
    return set;
  }

  function expandedSet(baseSet) {
    const expanded = new Set();
    baseSet.forEach((term) => expandTerm(term).forEach((value) => expanded.add(value)));
    return expanded;
  }

  function queryTerms(query) {
    return normalize(query)
      .split(' ')
      .map((term) => singularize(term))
      .filter((term) => term.length > 1 && !STOP_WORDS.has(term));
  }

  function expandTerm(term) {
    const normalizedTerm = normalize(term);
    const singularTerm = singularize(normalizedTerm);
    const variants = new Set([normalizedTerm, singularTerm]);

    SYNONYM_GROUPS.forEach((group) => {
      const normalizedGroup = Array.from(normalizedSetFrom(group));
      if (normalizedGroup.includes(normalizedTerm) || normalizedGroup.includes(singularTerm)) {
        group.forEach((word) => {
          const normalizedWord = normalize(word);
          if (!normalizedWord) return;
          variants.add(normalizedWord);
          variants.add(singularize(normalizedWord));
        });
      }
    });

    if (normalizedTerm.length > 3 && !normalizedTerm.endsWith('s')) variants.add(`${normalizedTerm}s`);
    if (singularTerm.length > 3 && !singularTerm.endsWith('s')) variants.add(`${singularTerm}s`);

    return Array.from(variants).filter(Boolean);
  }

  const EXPANDED_CATEGORY_TERMS = expandedSet(CATEGORY_TERMS);
  const EXPANDED_COUNTRY_TERMS = expandedSet(COUNTRY_TERMS);
  const EXPANDED_COLOR_TERMS = expandedSet(COLOR_TERMS);

  function termKind(term) {
    const variants = expandTerm(term);
    if (variants.some((variant) => EXPANDED_CATEGORY_TERMS.has(variant))) return 'category';
    if (variants.some((variant) => EXPANDED_COUNTRY_TERMS.has(variant))) return 'country';
    if (variants.some((variant) => EXPANDED_COLOR_TERMS.has(variant))) return 'color';
    return 'general';
  }

  function containsWholeTerm(haystack, term) {
    const text = ` ${normalize(haystack)} `;
    return expandTerm(term).some((variant) => {
      const escaped = variant.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
      return new RegExp(`(^|\\s)${escaped}(\\s|$)`).test(text);
    });
  }

  function textFromCard(card) {
    const titleNode = card.querySelector('.product-card-title');
    const linkNode = card.querySelector('a[href*="/products/"]');
    return [
      card.dataset.searchText,
      card.dataset.searchTitle,
      card.dataset.searchType,
      card.dataset.searchVendor,
      card.dataset.searchTags,
      card.dataset.searchBody,
      titleNode ? titleNode.textContent : '',
      titleNode ? titleNode.getAttribute('title') : '',
      linkNode ? linkNode.getAttribute('href') : ''
    ].join(' ');
  }

  function strongTextFromCard(card) {
    const titleNode = card.querySelector('.product-card-title');
    const linkNode = card.querySelector('a[href*="/products/"]');
    return [
      card.dataset.searchText,
      card.dataset.searchTitle,
      card.dataset.searchType,
      card.dataset.searchVendor,
      card.dataset.searchTags,
      titleNode ? titleNode.textContent : '',
      titleNode ? titleNode.getAttribute('title') : '',
      linkNode ? linkNode.getAttribute('href') : ''
    ].join(' ');
  }

  function productKey(card) {
    const linkNode = card.querySelector('a[href*="/products/"]');
    if (!linkNode) return textFromCard(card);
    try {
      const url = new URL(linkNode.getAttribute('href'), window.location.origin);
      return url.pathname.replace(/\/collections\/[^/]+\/products\//, '/products/');
    } catch (error) {
      return linkNode.getAttribute('href');
    }
  }

  function scoreCard(card, query) {
    const terms = queryTerms(query);
    const kinds = terms.map(termKind);
    const hasCategoryTerm = kinds.includes('category');
    const phrase = normalize(query);
    const title = normalize(card.dataset.searchTitle || card.querySelector('.product-card-title')?.textContent || '');
    const type = normalize(card.dataset.searchType || '');
    const tags = normalize(card.dataset.searchTags || '');
    const vendor = normalize(card.dataset.searchVendor || '');
    const body = normalize(card.dataset.searchBody || '');
    const strongText = normalize(strongTextFromCard(card));
    const broadText = normalize([strongText, body].join(' '));

    let score = 0;
    let strictMatch = true;

    if (phrase && title === phrase) score += 700;
    if (phrase && title.includes(phrase)) score += 350;
    if (phrase && type.includes(phrase)) score += 200;
    if (phrase && tags.includes(phrase)) score += 120;

    terms.forEach((term, index) => {
      const kind = kinds[index];
      const strongMatch = containsWholeTerm(strongText, term);
      const broadMatch = containsWholeTerm(broadText, term);
      const bodyMatch = containsWholeTerm(body, term);

      if (kind === 'category') {
        if (!strongMatch) strictMatch = false;
      } else if (kind === 'country' || kind === 'color') {
        if (hasCategoryTerm) {
          if (!broadMatch) strictMatch = false;
        } else if (!strongMatch) {
          strictMatch = false;
        }
      } else if (!broadMatch) {
        strictMatch = false;
      }

      if (containsWholeTerm(title, term)) score += 60;
      if (containsWholeTerm(type, term)) score += 40;
      if (containsWholeTerm(tags, term)) score += 30;
      if (containsWholeTerm(vendor, term)) score += 14;
      if (strongMatch) score += 10;
      if (bodyMatch && kind !== 'category') score += 3;
      if (!strongMatch && bodyMatch && kind === 'category') score -= 200;
    });

    if (!strictMatch) score -= 2000;

    return {
      score,
      strictMatch,
      available: card.dataset.searchAvailable !== 'false'
    };
  }

  function queryVariants(query) {
    const terms = queryTerms(query);
    const variants = new Set();
    const original = normalize(query);
    if (original) variants.add(original);
    if (!terms.length) return Array.from(variants);

    const options = terms.map((term) => {
      const values = expandTerm(term)
        .filter((value) => value && !STOP_WORDS.has(value))
        .filter((value) => value.length > 1)
        .sort((a, b) => {
          if (a === term) return -1;
          if (b === term) return 1;
          return a.length - b.length;
        });
      return Array.from(new Set(values)).slice(0, 7);
    });

    function build(index, current) {
      if (variants.size >= 30) return;
      if (index >= options.length) {
        const built = current.join(' ').replace(/\s+/g, ' ').trim();
        if (built) variants.add(built);
        return;
      }
      options[index].forEach((option) => build(index + 1, current.concat(option)));
    }

    build(0, []);

    if (original.includes('knee pad')) {
      variants.add(original.replace('knee pads', 'kneepads'));
      variants.add(original.replace('knee pad', 'kneepad'));
    }

    return Array.from(variants).slice(0, 30);
  }

  function requiredUrlForPage(pageNumber, query) {
    const url = new URL(window.location.href);
    Object.entries(REQUIRED_SEARCH_PARAMS).forEach(([key, value]) => url.searchParams.set(key, value));
    url.searchParams.set('q', query);
    url.searchParams.set('page', pageNumber);
    return url.toString();
  }

  function updateCount(count, query) {
    const text = `${count} product${count === 1 ? '' : 's'} found for “${query}”`;
    document.querySelectorAll('#ProductCount .facets__label, .mobile-filter-count .facets__label').forEach((element) => {
      element.textContent = text;
    });
  }

  function hideOriginalHeaderCount() {
    document.querySelectorAll('.template-header--description').forEach((element) => {
      if (/result|found|product/i.test(element.textContent || '')) element.remove();
    });
  }

  function setEmptyState(grid, show, query) {
    let empty = document.getElementById('grStrictSearchEmpty');
    if (!empty) {
      empty = document.createElement('div');
      empty.id = 'grStrictSearchEmpty';
      empty.className = 'collection-empty gr-strict-search-empty';
      grid.insertAdjacentElement('afterend', empty);
    }
    empty.innerHTML = `<p>No products matched “${escapeHtml(query)}”. Try a shorter product name, color, country, or product type.</p>`;
    empty.style.display = show ? '' : 'none';
    grid.style.display = show ? 'none' : '';
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>'"]/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    }[char]));
  }

  function clearGrid(grid) {
    grid.querySelectorAll(':scope > .column').forEach((item) => item.remove());
  }

  function renderPage(grid, items, pageNumber, shouldPushState = false) {
    const perPage = Math.max(parseInt(grid.dataset.productsPerPage || '12', 10), 1);
    const paginationType = grid.dataset.paginationType || 'paginated';
    const totalPages = Math.max(Math.ceil(items.length / perPage), 1);
    const page = Math.min(Math.max(parseInt(pageNumber || '1', 10), 1), totalPages);
    const loadingOverlay = grid.querySelector(':scope > .loading-overlay');
    const start = (page - 1) * perPage;
    const pageItems = (paginationType === 'loadmore' || paginationType === 'infinite')
      ? items.slice(0, page * perPage)
      : items.slice(start, start + perPage);

    isRendering = true;
    clearGrid(grid);

    const fragment = document.createDocumentFragment();
    pageItems.forEach((item) => fragment.appendChild(item.card.cloneNode(true)));

    if (loadingOverlay) {
      grid.insertBefore(fragment, loadingOverlay);
    } else {
      grid.appendChild(fragment);
    }

    setEmptyState(grid, items.length === 0, grid.dataset.searchTerms || new URLSearchParams(window.location.search).get('q') || '');
    renderPagination(grid, items, page, paginationType);

    if (shouldPushState) {
      const url = new URL(window.location.href);
      Object.entries(REQUIRED_SEARCH_PARAMS).forEach(([key, value]) => url.searchParams.set(key, value));
      if (page > 1) {
        url.searchParams.set('page', page);
      } else {
        url.searchParams.delete('page');
      }
      window.history.pushState({ grStrictSearchPage: page }, '', url.toString());
    }

    window.setTimeout(() => {
      isRendering = false;
    }, 0);
  }

  function renderPagination(grid, items, currentPage, paginationType) {
    const perPage = Math.max(parseInt(grid.dataset.productsPerPage || '12', 10), 1);
    const totalPages = Math.ceil(items.length / perPage);
    const originalPagination = document.querySelector('pagination-theme');
    let container = document.getElementById('grStrictSearchPagination');

    if (originalPagination) originalPagination.style.display = 'none';

    if (!container) {
      container = document.createElement('div');
      container.id = 'grStrictSearchPagination';
      container.className = 'gr-strict-search-pagination';
      (originalPagination || grid).insertAdjacentElement('afterend', container);
    }

    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    if (paginationType === 'loadmore' || paginationType === 'infinite') {
      const shown = Math.min(currentPage * perPage, items.length);
      const hasMore = shown < items.length;
      container.innerHTML = hasMore ? '<div class="row"><div class="small-12 columns"><button class="load-more button" type="button"><span>Load more</span></button></div></div>' : '';
      const button = container.querySelector('.load-more');
      if (button) {
        button.addEventListener('click', () => renderPage(grid, items, currentPage + 1, true));
      }
      if (paginationType === 'infinite' && button && 'IntersectionObserver' in window) {
        const infiniteObserver = new IntersectionObserver((entries) => {
          if (entries[0] && entries[0].isIntersecting) {
            infiniteObserver.disconnect();
            renderPage(grid, items, currentPage + 1, true);
          }
        }, { threshold: 1 });
        infiniteObserver.observe(button);
      }
      return;
    }

    const parts = [];
    parts.push(`<span class="prev"><a href="#" data-gr-page="${Math.max(currentPage - 1, 1)}" ${currentPage === 1 ? 'aria-disabled="true"' : ''}>‹<span class="visually-hidden">Previous</span></a></span>`);

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 2) {
        parts.push(`<span class="page ${i === currentPage ? 'current' : ''}">${i === currentPage ? i : `<a href="#" data-gr-page="${i}">${i}</a>`}</span>`);
      } else if (i === 2 || i === totalPages - 1) {
        parts.push('<span class="page dots">…</span>');
      }
    }

    parts.push(`<span class="next"><a href="#" data-gr-page="${Math.min(currentPage + 1, totalPages)}" ${currentPage === totalPages ? 'aria-disabled="true"' : ''}>›<span class="visually-hidden">Next</span></a></span>`);

    container.innerHTML = `<div class="pagination pagination-type--paginated"><div class="row"><div class="small-12 columns"><div class="page-numbers nav-links">${parts.join('')}</div></div></div></div>`;
    container.querySelectorAll('[data-gr-page]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        if (link.getAttribute('aria-disabled') === 'true') return;
        renderPage(grid, items, parseInt(link.dataset.grPage, 10), true);
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  async function fetchDocumentForPage(pageNumber, query) {
    const response = await fetch(requiredUrlForPage(pageNumber, query), { credentials: 'same-origin' });
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  async function fetchCardsForVariant(query) {
    const firstDoc = await fetchDocumentForPage(1, query);
    const firstGrid = firstDoc.querySelector('#product-grid[data-gr-strict-search-grid="true"]');
    const totalPages = firstGrid ? Math.max(parseInt(firstGrid.dataset.totalPages || '1', 10), 1) : 1;
    const cards = Array.from(firstDoc.querySelectorAll('#product-grid > .column[data-search-card="true"]'));

    if (totalPages > 1) {
      const remainingDocs = await Promise.all(Array.from({ length: totalPages - 1 }, (_, index) => fetchDocumentForPage(index + 2, query)));
      remainingDocs.forEach((doc) => {
        cards.push(...Array.from(doc.querySelectorAll('#product-grid > .column[data-search-card="true"]')));
      });
    }

    return cards;
  }

  async function runStrictSearch() {
    const grid = document.querySelector('#product-grid[data-gr-strict-search-grid="true"]');
    if (!grid || !window.location.pathname.includes('/search')) return;

    const params = new URLSearchParams(window.location.search);
    const query = params.get('q') || grid.dataset.searchTerms || '';
    if (!query.trim()) return;

    const runId = ++latestRun;
    const currentPage = Math.max(parseInt(params.get('page') || grid.dataset.currentPage || '1', 10), 1);
    const variants = queryVariants(query);

    hideOriginalHeaderCount();
    grid.classList.add('loading');

    try {
      const allCardsByVariant = await Promise.all(variants.map((variant) => fetchCardsForVariant(variant)));
      if (runId !== latestRun) return;

      const seen = new Set();
      const scoredItems = [];

      allCardsByVariant.flat().forEach((card, index) => {
        const key = productKey(card);
        if (seen.has(key)) return;
        seen.add(key);

        const scored = scoreCard(card, query);
        if (!scored.strictMatch) return;

        scoredItems.push({
          card,
          index,
          score: scored.score,
          available: scored.available
        });
      });

      scoredItems.sort((a, b) => {
        if (a.available !== b.available) return a.available ? -1 : 1;
        if (b.score !== a.score) return b.score - a.score;
        return a.index - b.index;
      });

      grid.dataset.searchTerms = query;
      updateCount(scoredItems.length, query);
      renderPage(grid, scoredItems, currentPage);
    } catch (error) {
      console.warn('Gear Rack strict search could not finish:', error);
    } finally {
      grid.classList.remove('loading');
    }
  }

  function ensureRequiredParams() {
    if (!window.location.pathname.includes('/search')) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.get('q')) return;

    let changed = false;
    Object.entries(REQUIRED_SEARCH_PARAMS).forEach(([key, value]) => {
      if (url.searchParams.get(key) !== value) {
        url.searchParams.set(key, value);
        changed = true;
      }
    });

    if (changed) window.location.replace(url.toString());
  }

  function observeFacetRenders() {
    const target = document.getElementById('ProductGridContainer');
    if (!target || observer) return;

    observer = new MutationObserver(() => {
      if (isRendering) return;
      window.clearTimeout(observeFacetRenders.timeout);
      observeFacetRenders.timeout = window.setTimeout(runStrictSearch, 150);
    });

    observer.observe(target, { childList: true, subtree: true });
  }

  ensureRequiredParams();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      observeFacetRenders();
      runStrictSearch();
    });
  } else {
    observeFacetRenders();
    runStrictSearch();
  }

  window.addEventListener('popstate', () => {
    window.setTimeout(runStrictSearch, 50);
  });
})();
